// src/lib/workQueue.js
// ---------------------------------------------------------------------------
// Modelo de la Cola de Trabajo (S3). Lógica PURA y compartida entre el cliente y
// las Cloud Functions: las funciones no pueden importar de `src/`, así que
// `functions/lib/workQueue.js` re-declara lo mínimo y ESTE archivo es la
// referencia. Cualquier cambio de reglas se hace en los dos, con el mismo test.
//
// Estación = `machines.category`, decisión de Daniel 2026-09-02. Verificado
// contra producción ese día: 14 máquinas → IMPRESION 5 · CORTE 4 · FINISHING 2
// · LOGISTICA 2, más "Finalizado" en Proceso Externo, que queda FUERA.
// ---------------------------------------------------------------------------

export const STATIONS = ['IMPRESION', 'CORTE', 'FINISHING', 'LOGISTICA'];

export const STATION_LABEL = {
    IMPRESION: 'Impresión',
    CORTE: 'Corte',
    FINISHING: 'Finishing',
    LOGISTICA: 'Logística',
};

// category → estación. `null` = existe pero NO entra en la cola.
export const STATION_BY_CATEGORY = {
    'Impresión': 'IMPRESION',
    'Corte': 'CORTE',
    'Acabado': 'FINISHING',
    'Proceso Manual': 'FINISHING',
    'Almacén': 'LOGISTICA',
    'Proceso Externo': null,
};

/** Estación de una máquina, o null si su categoría no entra (o no se reconoce). */
export const stationOfMachine = (machine) => {
    const st = STATION_BY_CATEGORY[machine?.category];
    return st === undefined ? null : st;
};

/** Máquinas de una estación, ordenadas por nombre (son las columnas del tablero). */
export function machinesForStation(machines = [], station) {
    return (machines || [])
        .filter((m) => stationOfMachine(m) === station)
        .sort((a, b) => String(a.name || '').localeCompare(String(b.name || '')));
}

// Fases del job que generan fichas. Case-insensitive a propósito: en producción
// conviven 'Pendiente' y 'PENDIENTE' (J-1155) — el audit lo dejó anotado y aquí
// no se normaliza el dato, se normaliza la comparación.
const PHASES_CON_COLA = ['en progreso', 'pendiente'];

/** ¿La fase del job genera / mantiene fichas de cola? */
export const phaseGeneratesQueue = (phase) =>
    PHASES_CON_COLA.includes(String(phase || '').trim().toLowerCase());

/** ¿Dos fases son la misma? Comparación insensible a mayúsculas y espacios. */
export const samePhase = (a, b) =>
    String(a || '').trim().toLowerCase() === String(b || '').trim().toLowerCase();

export const QUEUE_STATUSES = ['queued', 'in_progress', 'done', 'stalled'];

/**
 * Total de repeticiones que le toca a cada estación, a partir de las planchas.
 * IMPRESION suma las que imprimen; CORTE las que llevan corte. FINISHING y
 * LOGISTICA no se derivan de las planchas — no hay dato que lo sostenga — así
 * que no se crean fichas para ellas en el trigger (ver `queueDocsForJob`).
 *
 * @param {object[]} sheets
 * @return {{IMPRESION:number, CORTE:number}}
 */
export function stationTotals(sheets = []) {
    let IMPRESION = 0;
    let CORTE = 0;
    (sheets || []).forEach((s) => {
        const reps = Number(s?.repetitions) || 0;
        if (s?.isPrinting) IMPRESION += reps;
        if (s?.hasCut !== false) CORTE += reps;      // legado: sin el flag, lleva corte
    });
    return { IMPRESION, CORTE };
}

/**
 * Repeticiones ya hechas por estación. Impresión a 2 caras cuenta el mínimo
 * entre frente y dorso, igual que `printedDone` de production.js: una plancha
 * con el frente listo y el dorso a medias NO está impresa.
 *
 * @param {object[]} sheets
 * @return {{IMPRESION:number, CORTE:number}}
 */
export function stationDone(sheets = []) {
    let IMPRESION = 0;
    let CORTE = 0;
    (sheets || []).forEach((s) => {
        const reps = Number(s?.repetitions) || 0;
        if (s?.isPrinting) {
            const dosCaras = Number(s?.printFaces) === 2;
            const frente = Number(s?.printedCount) || 0;
            const dorso = Number(s?.printedCountBack) || 0;
            IMPRESION += Math.min(dosCaras ? Math.min(frente, dorso) : frente, reps);
        }
        if (s?.hasCut !== false) CORTE += Math.min(Number(s?.cutCount) || 0, reps);
    });
    return { IMPRESION, CORTE };
}

/** Id determinista de la ficha: una por (job, estación). Hace idempotente el alta. */
export const queueDocId = (jobId, station) => `${jobId}__${station}`;

/**
 * Fichas que le corresponden a un job. Solo IMPRESION y CORTE: son las únicas
 * dos estaciones cuyo total se puede derivar de las planchas. Una estación con
 * total 0 (p. ej. un job que no imprime nada) NO genera ficha — una barra 0/0
 * no informa de nada.
 *
 * @param {object} job   doc de `jobs` (con id)
 * @param {object[]} sheets
 * @param {string} clientName
 * @return {object[]} documentos listos para escribir (sin createdAt/createdBy)
 */
export function queueDocsForJob(job, sheets = [], clientName = '') {
    const totals = stationTotals(sheets);
    const done = stationDone(sheets);
    return ['IMPRESION', 'CORTE']
        .filter((st) => totals[st] > 0)
        .map((station) => ({
            id: queueDocId(job.id, station),
            jobId: job.id,
            jobNumber: job.jobNumber ?? null,
            clientName,
            station,
            machineId: null,           // nace sin máquina: el humano arrastra (S3)
            order: 0,
            status: done[station] >= totals[station] ? 'done' : 'queued',
            sheetsTotal: totals[station],
            sheetsDone: Math.min(done[station], totals[station]),
            lastScanAt: null,
        }));
}

/** Estado derivado del avance. No pisa 'stalled' salvo que la ficha ya esté completa. */
export function statusFromProgress(sheetsDone, sheetsTotal, statusActual) {
    if (sheetsTotal > 0 && sheetsDone >= sheetsTotal) return 'done';
    if (statusActual === 'done') return 'in_progress';   // se reabrió (reproducción)
    if (statusActual === 'stalled') return 'stalled';    // lo destraba el scheduled o el humano
    return sheetsDone > 0 ? 'in_progress' : 'queued';
}

/**
 * Orden de una ficha al soltarla en una columna. `order` es un number y se
 * reasigna a TODA la columna destino, que es lo único que sobrevive a dos
 * personas arrastrando a la vez sin un servidor de por medio.
 *
 * @param {object[]} fichasDeColumna  las de la columna destino, ya ordenadas
 * @param {string} movedId
 * @param {number} destIndex
 * @return {{id:string, order:number}[]} solo las que cambian
 */
export function reorderColumn(fichasDeColumna = [], movedId, destIndex) {
    const sin = fichasDeColumna.filter((f) => f.id !== movedId);
    const movida = fichasDeColumna.find((f) => f.id === movedId);
    const lista = movida
        ? [...sin.slice(0, destIndex), movida, ...sin.slice(destIndex)]
        : sin;
    return lista
        .map((f, i) => ({ id: f.id, order: i }))
        .filter((n) => {
            const original = fichasDeColumna.find((f) => f.id === n.id);
            return !original || original.order !== n.order;
        });
}

/** Ordena las fichas de una columna: por `order`, y a igualdad por jobNumber. */
export const sortQueue = (fichas = []) =>
    [...(fichas || [])].sort((a, b) =>
        (Number(a.order) || 0) - (Number(b.order) || 0)
        || (Number(a.jobNumber) || 0) - (Number(b.jobNumber) || 0));

/** Horas sin escaneo tras las cuales una ficha in_progress se marca `stalled`. */
export const STALLED_HOURS = 48;

/** ¿La ficha está estancada? Solo aplica a `in_progress` con algún escaneo previo. */
export function isStalled(ficha, ahora = new Date(), horas = STALLED_HOURS) {
    if (!ficha || ficha.status !== 'in_progress') return false;
    const last = ficha.lastScanAt?.toDate ? ficha.lastScanAt.toDate() : ficha.lastScanAt;
    if (!(last instanceof Date) || Number.isNaN(last.getTime())) return false;
    return (ahora - last) > horas * 3600 * 1000;
}

// ─── S4a · Solicitudes (2026-09-02) ─────────────────────────────────────────
// Una SOLICITUD es una ficha de `work_queue` con station 'SOLICITUD' y SIN job:
// vive en el mismo tablero, pero sus columnas son etapas fijas (`stage`), no
// máquinas. Al convertirse en trabajo recibe `jobId` y pasa a `done`.

export const SOLICITUD_STATION = 'SOLICITUD';

export const SOLICITUD_STAGES = [
    { key: 'nueva', label: 'Nueva', dot: 'bg-gray-400' },
    { key: 'diseno', label: 'Diseño', dot: 'bg-violet-400' },
    { key: 'proof', label: 'Proof', dot: 'bg-amber-400' },
    { key: 'lista', label: 'Lista', dot: 'bg-green-500' },
];

/** Normaliza un stage: cualquier valor desconocido cae en 'nueva', no desaparece. */
export const normalizeStage = (stage) =>
    SOLICITUD_STAGES.some((s) => s.key === stage) ? stage : 'nueva';

export const isSolicitud = (ficha) => ficha?.station === SOLICITUD_STATION;

/**
 * ¿Se puede convertir en trabajo? Solo desde la columna Lista, y solo si aún no
 * se convirtió (una solicitud convertida queda `done` y con `jobId`).
 */
export const canConvertSolicitud = (ficha) =>
    isSolicitud(ficha)
    && normalizeStage(ficha?.stage) === 'lista'
    && ficha?.status !== 'done'
    && !ficha?.jobId;

/**
 * Regla de oro del intake (aplica en el formulario de solicitud y en NewJob):
 * imagen + cliente + nombre, los tres obligatorios. Devuelve la lista de
 * errores en el orden en que se muestran; [] si está completa.
 */
export function validateSolicitud({ imageUrl, clientId, jobName } = {}) {
    const errores = [];
    if (!imageUrl) errores.push('La imagen es obligatoria.');
    if (!clientId) errores.push('Elige un cliente (o créalo aquí mismo).');
    if (!String(jobName || '').trim()) errores.push('Escribe el nombre de la solicitud.');
    return errores;
}

/** Documento de solicitud listo para escribir (sin createdAt/createdBy). */
export function solicitudDoc({ imageUrl, clientId, clientName, jobName, note }) {
    return {
        jobId: null,
        jobNumber: null,
        clientId: clientId || null,
        clientName: clientName || '',
        station: SOLICITUD_STATION,
        stage: 'nueva',
        machineId: null,
        order: 0,
        status: 'queued',
        sheetsTotal: 0,
        sheetsDone: 0,
        lastScanAt: null,
        imageUrl: imageUrl || '',
        jobName: String(jobName || '').trim(),
        note: String(note || '').trim(),
    };
}
